import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useAuth } from "../lib/auth";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/sustainable-packaging", label: "Sustainability" },
    { to: "/industries", label: "Industries" },
    { to: "/custom-printing", label: "Custom Print" },
    { to: "/export", label: "Export" },
    { to: "/blog", label: "Blog" },
];

const PRODUCT_LINKS = [
    { to: "/products/paper-salad-bowls", label: "Paper Salad Bowls" },
    { to: "/products/paper-soup-bowls", label: "Paper Soup Bowls" },
    { to: "/products/pet-flat-lids", label: "PET Flat Lids" },
];

export default function Nav() {
    const [open, setOpen] = useState(false);
    const [productsOpen, setProductsOpen] = useState(false);
    const location = useLocation();
    const { user } = useAuth();

    useEffect(() => { setOpen(false); setProductsOpen(false); }, [location.pathname]);

    return (
        <header className="sticky top-0 z-50 bg-bone border-b border-forest/20" data-testid="site-header">
            <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
                    <img src="https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/sgmw02mn_Tm%20Png-08.webp" alt="The Puro Pack — Your Brand, Our Eco Promise" className="h-10 lg:h-12 w-auto" />
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {LINKS.slice(0, 2).map((l) => (
                        <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className={({ isActive }) => `text-sm text-forest hover:text-clay transition-colors ${isActive ? "text-clay" : ""}`}>
                            {l.label}
                        </NavLink>
                    ))}
                    <div className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
                        <button className="text-sm text-forest hover:text-clay transition-colors flex items-center gap-1" data-testid="nav-products">
                            Products <span className="font-mono text-xs">↓</span>
                        </button>
                        {productsOpen && (
                            <div className="absolute top-full left-0 pt-4">
                                <div className="bg-bone border border-forest min-w-[260px] py-2">
                                    {PRODUCT_LINKS.map((p) => (
                                        <NavLink key={p.to} to={p.to} data-testid={`nav-product-${p.to.split('/').pop()}`} className="block px-5 py-2 text-sm text-forest hover:bg-forest hover:text-bone transition-colors">
                                            {p.label}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    {LINKS.slice(2).map((l) => (
                        <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`} className={({ isActive }) => `text-sm text-forest hover:text-clay transition-colors ${isActive ? "text-clay" : ""}`}>
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    {user?.role === "admin" && (
                        <Link to="/admin" data-testid="nav-admin" className="hidden lg:inline-block text-xs font-mono uppercase tracking-wider text-clay hover:text-forest">
                            Admin
                        </Link>
                    )}
                    <Link to="/contact" className="hidden lg:inline-flex btn-pill btn-solid" data-testid="nav-contact-cta">
                        Enquire →
                    </Link>
                    <button onClick={() => setOpen((s) => !s)} className="lg:hidden text-forest" data-testid="nav-mobile-toggle" aria-label="Toggle menu">
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-forest/20 bg-bone">
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {LINKS.slice(0, 2).map((l) => (
                            <Link key={l.to} to={l.to} className="text-forest text-lg font-serif" data-testid={`nav-mobile-${l.label.toLowerCase()}`}>{l.label}</Link>
                        ))}
                        <div className="pt-2">
                            <div className="font-mono text-xs uppercase tracking-wider text-stone mb-3">Products</div>
                            {PRODUCT_LINKS.map((p) => (
                                <Link key={p.to} to={p.to} className="block py-1.5 text-forest" data-testid={`nav-mobile-product-${p.to.split('/').pop()}`}>{p.label}</Link>
                            ))}
                        </div>
                        {LINKS.slice(2).map((l) => (
                            <Link key={l.to} to={l.to} className="text-forest text-lg font-serif" data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>{l.label}</Link>
                        ))}
                        <Link to="/contact" className="btn-pill btn-solid w-fit" data-testid="nav-mobile-contact-cta">Enquire →</Link>
                    </div>
                </div>
            )}
        </header>
    );
}
